import { useMemo } from "react";
import Button from "@components/ui/Button";

import PurposeTabs from "@components/shared/PurposeTabs";
import ErrorBanner from "@components/shared/ErrorBanner";
import QuestionsPreview from "@components/shared/QuestionsPreview";

export default function DefaultFlowManager({
  purposes,
  purpose,
  setPurpose,

  draftFlow,
  setDraftFlow,

  onSave,
  saving,
  canSave,

  library,
  libraryByEnum,

  error,
  onClearError,
}) {
  const list = draftFlow || [];

  const used = useMemo(
    () => new Set(list.map((x) => x.agent_question_enum)),
    [list]
  );

  const availableToAdd = useMemo(() => {
    return library.filter((q) => !used.has(q.agent_question_enum));
  }, [library, used]);

  const addEnum = (agent_question_enum) => {
    if (!agent_question_enum) return;
    onClearError?.();
    setDraftFlow([...list, { agent_question_enum, order: list.length + 1 }]);
  };

  const move = (idx, dir) => {
    const next = [...list];
    const to = idx + dir;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    onClearError?.();
    setDraftFlow(next);
  };

  const removeAt = (idx) => {
    onClearError?.();
    setDraftFlow(list.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-lg font-semibold">Default Flows</div>
        <div className="text-xs text-gray-500">
          Define the default ordered questions for each purpose. Clinics can override later.
        </div>
      </div>

      <ErrorBanner message={error?.message} onDismiss={onClearError} />

      <PurposeTabs purposes={purposes} value={purpose} onChange={setPurpose} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor */}
        <div className="border rounded-lg bg-white">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">{purpose} default flow</div>
              <div className="text-xs text-gray-500">{list.length} question(s)</div>
            </div>

            <Button
              type="button"
              color="primary"
              disabled={!canSave}
              onClick={onSave}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Add question
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                defaultValue=""
                onChange={(e) => {
                  addEnum(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="" disabled>
                  Select from library…
                </option>
                {availableToAdd.map((q) => (
                  <option key={q.agent_question_enum} value={q.agent_question_enum}>
                    {q.question} ({q.agent_question_enum})
                  </option>
                ))}
              </select>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600">
                Ordered Questions
              </div>

              {list.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">No questions added.</div>
              ) : (
                <div className="divide-y">
                  {list.map((item, idx) => {
                    const q = libraryByEnum.get(item.agent_question_enum);
                    return (
                      <div
                        key={`${item.agent_question_enum}-${idx}`}
                        className="p-3 flex gap-3"
                      >
                        <div className="w-8 text-xs text-gray-500 pt-1">{idx + 1}</div>

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {q?.question ?? item.agent_question_enum}
                          </div>
                          <div className="text-xs text-gray-500 break-all">
                            {item.agent_question_enum}
                          </div>

                          {q?.rules?.length ? (
                            <div className="mt-2 text-xs text-gray-600">
                              <span className="font-medium">Rules:</span>{" "}
                              {q.rules.join(" • ")}
                            </div>
                          ) : null}
                        </div>

                        <div className="flex flex-col gap-2">
                          <Button
                            type="button"
                            size="xs"
                            color="light"
                            onClick={() => move(idx, -1)}
                            disabled={idx === 0}
                          >
                            Move Up
                          </Button>
                          <Button
                            type="button"
                            size="xs"
                            color="light"
                            onClick={() => move(idx, +1)}
                            disabled={idx === list.length - 1}
                          >
                            Move Down
                          </Button>
                          <Button
                            type="button"
                            size="xs"
                            color="danger"
                            onClick={() => removeAt(idx)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500">
              Changes are not saved until you click <span className="font-medium">Save</span>.
            </div>
          </div>
        </div>

        {/* Preview */}
        <QuestionsPreview
          title={`Preview (${purpose})`}
          list={list}
          libraryByEnum={libraryByEnum}
        />
      </div>
    </div>
  );
}
